import {useQuery, useInfiniteQuery, useQueryClient, useMutation} from "react-query";
import {toast} from 'react-toastify';
import API from './utils/api';
import {
    EmployeePayInfoDto, EmployeePayInfoPaginator,
    CreateEmployeePayInfoDto, QueryEmployeePayInfoDto,
    UpdateEmployeePayInfoDto
} from "../models/employee-pay-info.dto";
import ENDPOINTS from "./utils/endpoints";
import {QueryOptionsDto} from "../models/generic.dto";

export function useGetEmployeePayInfo(options?: QueryEmployeePayInfoDto) {
    const url = ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_PAY_INFO.GET;
    const request = (options: QueryEmployeePayInfoDto) => API.ProcessingCompanyAPI.getEmployeePayInfo(options);

    const {
        data,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        hasPreviousPage,
        fetchPreviousPage,
    } = useInfiniteQuery<EmployeePayInfoPaginator, Error>(
        [url, options],
        ({ queryKey, pageParam }) => request(Object.assign({}, queryKey[1], pageParam)),
        {...API.Handlers.pageParamOption}
    );

    return API.Handlers.paginationHandler(data,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        hasPreviousPage,
        fetchPreviousPage)
}

export function useGetEmployeePayInfoById(id: number) {
    const url = ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_PAY_INFO.DETAIL;
    const request = API.ProcessingCompanyAPI.getEmployeePayInfoById(id);

    const {data, isLoading, error} = useQuery<{ data:EmployeePayInfoDto }, Error>(
        [url, id],
        (() => request),
        {
            enabled: !!(id),
        }
    );

    return {
        data: data,
        isLoading,
        error,
    };
}
export function useCreateEmployeePayInfo(onSuccessCallback?: (responseData?: any) => void) {
    const queryClient = useQueryClient();

    return useMutation(
        ({payload}: {payload: CreateEmployeePayInfoDto}) => API.ProcessingCompanyAPI.createEmployeePayInfo(payload),
        {
            onSuccess: (response,) => {

                if(onSuccessCallback){
                    onSuccessCallback(response)
                }

                toast.success('Record created successfully');
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: () => {
                queryClient.invalidateQueries(ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_PAY_INFO.GET);
            },
        }
    )
}

export function useUpdateEmployeePayInfo() {
    const queryClient = useQueryClient();

    return useMutation(
        ({payload, id}: {payload: UpdateEmployeePayInfoDto, id: number}) => API.ProcessingCompanyAPI.updateEmployeePayInfo(id, payload),
        {
            onSuccess: () => {
                toast.success('Record updated successfully');
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: () => {
                queryClient.invalidateQueries(ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_PAY_INFO.GET);
            },
        }
    )
}

export function useDeleteEmployeePayInfo() {
    const queryClient = useQueryClient();

    return useMutation(
        ({ id}: {id: number}) => API.ProcessingCompanyAPI.deleteEmployeePayInfo(id),
        {
            onSuccess: () => {
                toast.success('Record deleted successfully');
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: () => {
                queryClient.invalidateQueries(ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_PAY_INFO.GET);
            },
        }
    )
}

export function useUploadEmployeePayInfo() {

    return useMutation(
            ({ payload, companyId }: { payload: FormData; companyId: number }) =>
                API.ProcessingCompanyAPI.uploadEmployeePayInfo(companyId, payload),
            {
                onSuccess: () => {
                },
                onError: API.Handlers.onErrorHandler,
                onSettled: () => {},
            }
        );
}